import { coreApi } from "./calls";

/**
 * Campaigns API
 * Centralized API calls for working with campaigns and banners
 */

/**
 * Fetch list of all campaigns
 * @param {Object} params - Filter parameters
 * @returns {Promise<Object>} List of campaigns with pagination
 */
export const fetchCampaigns = async (params = {}) => {
  try {
    const response = await coreApi.get("/campaigns", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching campaigns:", error);
    throw error;
  }
};

/**
 * Fetch a single campaign by ID
 * @param {number} id - Campaign ID
 * @returns {Promise<Object>} Campaign data
 */
export const fetchCampaign = async (id) => {
  try {
    const response = await coreApi.get(`/campaigns/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching campaign ${id}:`, error);
    throw error;
  }
};

/**
 * Create a new campaign
 * @param {Object} data - Campaign data
 * @returns {Promise<Object>} Created campaign
 */
export const createCampaign = async (data) => {
  try {
    const response = await coreApi.post("/campaigns", data);
    return response.data;
  } catch (error) {
    console.error("Error creating campaign:", error);
    throw error;
  }
};

/**
 * Update a campaign
 * @param {number} id - Campaign ID
 * @param {Object} data - Updated data
 * @returns {Promise<Object>} Updated campaign
 */
export const updateCampaign = async (id, data) => {
  try {
    const response = await coreApi.put(`/campaigns/${id}`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating campaign ${id}:`, error);
    throw error;
  }
};

/**
 * Delete a campaign
 * @param {number} id - Campaign ID
 * @returns {Promise<void>}
 */
export const deleteCampaign = async (id) => {
  try {
    await coreApi.delete(`/campaigns/${id}`);
  } catch (error) {
    console.error(`Error deleting campaign ${id}:`, error);
    throw error;
  }
};

/**
 * Toggle campaign active status
 * @param {number} id - Campaign ID
 * @returns {Promise<Object>} Updated campaign
 */
export const toggleCampaignStatus = async (id) => {
  try {
    const response = await coreApi.patch(`/campaigns/${id}/toggle`);
    return response.data;
  } catch (error) {
    console.error(`Error toggling campaign ${id} status:`, error);
    throw error;
  }
};

/**
 * Assign cities to campaign
 * @param {number} id - Campaign ID
 * @param {Array<number>} cityBranchIds - Array of city IDs
 * @returns {Promise<Object>} Updated campaign
 */
export const assignCitiesToCampaign = async (id, cityBranchIds) => {
  try {
    const response = await coreApi.post(`/campaigns/${id}/cities`, {
      city_branch_ids: cityBranchIds,
    });
    return response.data;
  } catch (error) {
    console.error(`Error assigning cities to campaign ${id}:`, error);
    throw error;
  }
};

/**
 * Upload banner for campaign
 * @param {number} campaignId - Campaign ID
 * @param {File} file - Image file
 * @param {string} bannerOrientationType - Banner orientation ('horizontal' | 'vertical')
 * @param {number} order - Banner display order (optional)
 * @returns {Promise<Object>} Created banner
 */
export const uploadBanner = async (campaignId, file, bannerOrientationType, order = null) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("banner_orientation_type", bannerOrientationType);
    if (order !== null) {
      formData.append("order", order);
    }

    const response = await coreApi.post(
      `/campaigns/${campaignId}/banners`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    return response.data;
  } catch (error) {
    console.error(`Error uploading banner for campaign ${campaignId}:`, error);
    throw error;
  }
};

/**
 * Delete a banner
 * @param {number} bannerId - Banner ID
 * @returns {Promise<void>}
 */
export const deleteBanner = async (bannerId) => {
  try {
    await coreApi.delete(`/banners/${bannerId}`);
  } catch (error) {
    console.error(`Error deleting banner ${bannerId}:`, error);
    throw error;
  }
};

/**
 * Reorder banners
 * @param {number} campaignId - Campaign ID
 * @param {Array<number>} bannerIds - Array of banner IDs in new order
 * @returns {Promise<void>}
 */
export const reorderBanners = async (campaignId, bannerIds) => {
  try {
    await coreApi.put(`/campaigns/${campaignId}/banners/reorder`, {
      banner_ids: bannerIds,
    });
  } catch (error) {
    console.error(`Error reordering banners for campaign ${campaignId}:`, error);
    throw error;
  }
};

/**
 * Fetch list of available banner positions
 * @returns {Promise<Array>} List of positions with descriptions
 */
export const fetchBannerPositions = async () => {
  try {
    const response = await coreApi.get("/campaigns/orientations");
    return response.data;
  } catch (error) {
    console.error("Error fetching banner positions:", error);
    throw error;
  }
};

/**
 * Fetch list of cities (city branches)
 * @returns {Promise<Array>} List of cities
 */
export const fetchCityBranches = async () => {
  try {
    const response = await coreApi.get("/cities");
    return response.data;
  } catch (error) {
    console.error("Error fetching city branches:", error);
    throw error;
  }
};

/**
 * Generate preview link for schedule with banners
 * POST /api/cleaning-schedule/preview-link
 * @param {Object} data - Data for link generation
 * @param {number} data.building_unit_id - Building unit ID (optional)
 * @param {Array<number>} data.city_branch_ids - City IDs (optional)
 * @param {string} data.date_from - Start date (optional)
 * @param {string} data.date_to - End date (optional)
 * @returns {Promise<Object>} Object with preview_url, token, period, building_unit
 */
export const generateSchedulePreviewLink = async (data) => {
  try {
    const response = await coreApi.post("/cleaning-schedule/preview-link", data);
    return response.data;
  } catch (error) {
    console.error("Error generating schedule preview link:", error);
    throw error;
  }
};
